import React from 'react';
import ReactDOM from 'react-dom';
import { Button, Typography, message, Alert, Select, Checkbox, Switch, Input, Spin, Tooltip } from 'antd';
import { X, Plus, Check, FlaskConical, Download } from 'lucide-react';
import { VideoItem } from '../constants/videos';
import { useSocialContext } from '../context/social';
import { isPublishable, SocialProvider, SocialAccount } from '../types/social';
import { ConnectModal } from './connect-modal';
import { tokens } from '../theme/tokens';
import { ONLY_TIKTOK_DEMO, HIDE_DOWNLOAD_ORIGINAL } from '../config/demo';
import { humanizePublishError } from '../utils/humanize-publish-error';

const { Text } = Typography;
const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export const PENDING_SHARE_KEY = 'drupi_pending_share';

// ── TikTok Content Posting API · creator info ───────────────────────────────
// Returned by GET /drupi/tiktok/{social_id}/creator-info. Drives the publish
// UX required by the audit (see TIKTOK_CONTENT_API_AUDIT.md).
interface CreatorInfo {
  creator_nickname: string;
  creator_username: string;
  creator_avatar_url: string;
  privacy_level_options: string[];
  comment_disabled: boolean;
  duet_disabled: boolean;
  stitch_disabled: boolean;
  max_video_post_duration_sec: number;
  // TikTok puede devolver 200 con un error code "intencional" (spam_risk_*, cupo diario, baneo):
  // el usuario NO puede publicar ahora. El backend lo manda acá; mostramos el motivo y bloqueamos.
  publish_blocked_code?: string | null;
}

// User-facing labels for TikTok privacy levels.
const PRIVACY_LABEL: Record<string, string> = {
  PUBLIC_TO_EVERYONE:    'Público (todos)',
  MUTUAL_FOLLOW_FRIENDS: 'Amigos (seguidores mutuos)',
  FOLLOWER_OF_CREATOR:   'Seguidores',
  SELF_ONLY:             'Privado (solo yo)',
};
const TIKTOK_TITLE_MAX = 2200;

const TikTokIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.87a8.18 8.18 0 0 0 4.78 1.52V6.93a4.85 4.85 0 0 1-1.01-.24z' />
  </svg>
);

const InstagramIcon = () => (
  <svg width='14' height='14' viewBox='0 0 24 24' fill='currentColor'>
    <path d='M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' />
  </svg>
);

const PROVIDER_ICON: Record<SocialProvider, React.ReactNode> = {
  tiktok:    <TikTokIcon />,
  instagram: <InstagramIcon />,
  google:    null,
  fake:      <FlaskConical size={14} />,
};

const PROVIDER_LABEL: Record<SocialProvider, string> = {
  tiktok:    'TikTok',
  instagram: 'Instagram',
  google:    'Google',
  fake:      'FakeSocialMedia',
};

interface AccountChipProps {
  account: SocialAccount;
  selected: boolean;
  onToggle: () => void;
}

const AccountChip: React.FC<AccountChipProps> = ({ account, selected, onToggle }) => (
  <button
    type='button'
    onClick={onToggle}
    title={`@${account.username} · ${PROVIDER_LABEL[account.provider]}`}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '10px 14px',
      borderRadius: 999,
      border: selected ? `2px solid ${tokens.colors.brand.blue}` : '2px solid rgba(0,0,0,0.12)',
      background: selected ? 'rgba(51, 63, 131, 0.08)' : '#ffffff',
      cursor: 'pointer',
      fontSize: 13,
      fontWeight: 600,
      color: tokens.colors.primary.neutral[800],
      opacity: selected ? 1 : 0.7,
      transition: 'all 0.15s ease',
      // Constraints so a TikTok open_id (40+ chars used as username) doesn't
      // blow out the chip past the modal width. The chip caps at 100% of its
      // wrap container; the username span inside takes the slack and truncates.
      maxWidth: '100%',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    {account.avatarUrl ? (
      <img src={account.avatarUrl} alt={account.username} style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
    ) : (
      <div style={{ width: 20, height: 20, borderRadius: '50%', background: tokens.colors.primary.neutral[200], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {PROVIDER_ICON[account.provider]}
      </div>
    )}
    <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{account.username}</span>
    <span style={{ color: tokens.colors.primary.neutral[400], fontWeight: 500, flexShrink: 0 }}>{PROVIDER_LABEL[account.provider]}</span>
    <span style={{ width: 18, height: 18, borderRadius: '50%', background: selected ? tokens.colors.brand.blue : 'transparent', border: selected ? 'none' : `2px solid ${tokens.colors.primary.neutral[300]}`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 2, flexShrink: 0 }}>
      {selected && <Check size={11} color='white' strokeWidth={3} />}
    </span>
  </button>
);

interface ShareModalProps {
  video: VideoItem;
  onClose: () => void;
}

// Shared util at src/utils/humanize-publish-error.ts handles all known TikTok
// + backend error codes; falls back to the raw string when nothing matches.
// Here we only need the `.message` (no expander UI in the share-modal toast).
const publishErrorMessage = (raw: string): string => {
  const h = humanizePublishError(raw);
  return h.message || 'Hubo un error al publicar. Intentá de nuevo más tarde.';
};

// Publish is now fire-and-forget (P10): we enqueue, toast, and close. The old
// 'waiting'/'success' phases + blocking poll were removed.
type PublishPhase = 'idle' | 'enqueueing' | 'error';

const PHASE_LABEL: Record<PublishPhase, string> = {
  idle:       '',
  enqueueing: 'Enviando...',
  error:      'Hubo un error al publicar',
};

export const ShareModal: React.FC<ShareModalProps> = ({ video, onClose }) => {
  const { accounts, authError, clearAuthError, loading } = useSocialContext();
  const [, contextHolder] = message.useMessage();
  const [phase, setPhase]       = React.useState<PublishPhase>('idle');
  const [phaseMsg, setPhaseMsg] = React.useState('');
  const [showConnect, setShowConnect] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);
  const abortRef = React.useRef<AbortController | null>(null);

  const publishableAccounts = React.useMemo(() => accounts.filter(a => isPublishable(a.provider)), [accounts]);
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(() => new Set(publishableAccounts.map(a => a.id)));

  // ── TikTok Content Posting API compliance state (audit requirements) ──────
  // Title editable by the user (P7). Pre-filled with the catalog caption.
  const [title, setTitle] = React.useState<string>(() => (video.title || '').slice(0, TIKTOK_TITLE_MAX));
  // Privacy level (P3). NO default — TikTok requires the user to manually pick
  // from a dropdown populated with privacy_level_options from creator_info.
  const [privacyLevel, setPrivacyLevel] = React.useState<string | undefined>(undefined);
  // Interaction settings — all OFF by default (TikTok: "none checked by default").
  const [allowComment, setAllowComment] = React.useState(false);
  const [allowDuet,    setAllowDuet]    = React.useState(false);
  const [allowStitch,  setAllowStitch]  = React.useState(false);
  // Commercial content disclosure (P6).
  const [disclose,       setDisclose]       = React.useState(false);
  const [yourBrand,      setYourBrand]      = React.useState(false);
  const [brandedContent, setBrandedContent] = React.useState(false);
  // Creator info (P1/P2/P4/P8) fetched fresh when a TikTok account is selected.
  const [creatorInfo,        setCreatorInfo]        = React.useState<CreatorInfo | null>(null);
  const [creatorInfoLoading, setCreatorInfoLoading] = React.useState(false);
  const [creatorInfoError,   setCreatorInfoError]   = React.useState<string | null>(null);
  // Actual video duration (P8), read from the <video> metadata.
  const [videoDuration, setVideoDuration] = React.useState<number | null>(null);

  React.useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      publishableAccounts.forEach(a => next.add(a.id));
      const valid = new Set(publishableAccounts.map(a => a.id));
      Array.from(next).forEach(id => { if (!valid.has(id)) next.delete(id); });
      return next;
    });
  }, [publishableAccounts]);

  // Limpiar polling al desmontar
  React.useEffect(() => () => { abortRef.current?.abort(); }, []);

  // Which selected accounts are TikTok (the compliance UX only applies to them).
  const selectedAccounts = React.useMemo(
    () => publishableAccounts.filter(a => selectedIds.has(a.id)),
    [publishableAccounts, selectedIds]
  );
  const firstTiktok = React.useMemo(
    () => selectedAccounts.find(a => a.provider === 'tiktok'),
    [selectedAccounts]
  );
  const hasTiktok = !!firstTiktok;

  // Fetch fresh creator info when a TikTok account is selected (audit P1):
  // "API Clients must retrieve the latest creator info when rendering the Post
  // to TikTok page." Re-fetches if the user switches TikTok account.
  React.useEffect(() => {
    if (!firstTiktok) { setCreatorInfo(null); setCreatorInfoError(null); return; }
    let cancelled = false;
    const ac = new AbortController();
    setCreatorInfoLoading(true);
    setCreatorInfoError(null);
    fetch(`${API_BASE}/drupi/tiktok/${firstTiktok.socialId}/creator-info`, { signal: ac.signal })
      .then(async r => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.detail || `HTTP ${r.status}`);
        }
        return r.json() as Promise<CreatorInfo>;
      })
      .then(info => {
        if (cancelled) return;
        setCreatorInfo(info);
        // TikTok requires disabled interactions to start unchecked AND stay
        // unselectable. If an interaction is disabled in the creator's settings,
        // keep our toggle off.
        if (info.comment_disabled) setAllowComment(false);
        if (info.duet_disabled)    setAllowDuet(false);
        if (info.stitch_disabled)  setAllowStitch(false);
      })
      .catch(e => {
        if (cancelled || (e as Error).name === 'AbortError') return;
        setCreatorInfoError(
          'No pudimos cargar la info de tu cuenta de TikTok. Reconectá la cuenta e intentá de nuevo.'
        );
      })
      .finally(() => { if (!cancelled) setCreatorInfoLoading(false); });
    return () => { cancelled = true; ac.abort(); };
  }, [firstTiktok]);

  // Branded Content ↔ privacy interaction (TikTok rule): Branded Content can
  // only be configured with public/friends visibility, never private (SELF_ONLY).
  // Bidirectional enforcement:
  //  - If privacy is SELF_ONLY (or not yet chosen), Branded Content is disabled.
  //  - If Branded Content is checked, the SELF_ONLY option is disabled in the
  //    privacy dropdown.
  const brandedContentAllowed = !!privacyLevel && privacyLevel !== 'SELF_ONLY';
  React.useEffect(() => {
    if (!brandedContentAllowed && brandedContent) setBrandedContent(false);
  }, [brandedContentAllowed, brandedContent]);

  const toggle = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  // Store the video so we can return to this flow after OAuth redirect
  const openConnect = () => {
    sessionStorage.setItem(PENDING_SHARE_KEY, JSON.stringify(video));
    setShowConnect(true);
  };

  // ── Publish validation (audit) ───────────────────────────────────────────
  const titleOk      = title.trim().length > 0;
  // Privacy must be explicitly selected for TikTok (no default value allowed).
  const privacyOk    = !hasTiktok || !!privacyLevel;
  // If the commercial-disclosure toggle is on, at least one option must be set.
  const commercialOk = !disclose || yourBrand || brandedContent;
  const maxDur       = creatorInfo?.max_video_post_duration_sec ?? 0;
  const durationOk   = !hasTiktok || maxDur <= 0 || videoDuration == null || videoDuration <= maxDur;
  // For TikTok, creator info must have loaded successfully before we can post.
  // TikTok puede devolver 200 con un code de bloqueo (spam_risk_*, cupo diario, baneo): no se
  // puede publicar. Lo humanizamos para mostrar el motivo y deshabilitamos Publicar.
  const publishBlock = creatorInfo?.publish_blocked_code
    ? humanizePublishError(creatorInfo.publish_blocked_code)
    : null;
  const tiktokReady  = !hasTiktok || (!!creatorInfo && !creatorInfoError && !creatorInfoLoading && !publishBlock);
  const busy         = phase === 'enqueueing';
  const canPublish   = selectedIds.size > 0 && titleOk && privacyOk && commercialOk && durationOk && tiktokReady && !busy;

  // Descarga el VIDEO ORIGINAL (MP4 sin optimizar). El catálogo reproduce HLS;
  // el backend deriva la key `uploads/.../video.mp4` desde la videoUrl del clip
  // y devuelve una URL presignada. 404 → no hay original (ej. videos migrados):
  // se avisa, no se rompe.
  const handleDownloadOriginal = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ video_url: video.b2_url || '' });
      if (video.title) qs.set('title', video.title);
      const res = await fetch(`${API_BASE}/drupi/catalog/original-download?${qs.toString()}`);
      if (res.status === 404) {
        message.info('No hay un video original descargable para este clip.');
        return;
      }
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        message.error(e?.detail || 'No se pudo preparar la descarga. Intentá de nuevo.');
        return;
      }
      const { url, filename } = await res.json();
      // Anchor con `download`: la URL presignada ya trae Content-Disposition
      // attachment, así que el navegador baja el archivo en vez de navegar.
      const a = document.createElement('a');
      a.href = url;
      if (filename) a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      message.error('No se pudo preparar la descarga. Intentá de nuevo.');
    } finally {
      setDownloading(false);
    }
  };

  const handlePublish = async () => {
    if (!canPublish) return;
    const targets = publishableAccounts.filter(a => selectedIds.has(a.id));

    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;

    setPhase('enqueueing');
    setPhaseMsg(PHASE_LABEL.enqueueing);

    // TikTok Content Posting API settings (audit). privacy_level is the value
    // the user selected from the dropdown (options come from creator_info).
    const tiktokSettings = {
      privacy_level:        privacyLevel,
      disable_comment:      !allowComment,
      disable_duet:         !allowDuet,
      disable_stitch:       !allowStitch,
      brand_organic_toggle: disclose && yourBrand,
      brand_content_toggle: disclose && brandedContent,
    };

    let enqueued = 0;
    for (const acc of targets) {
      try {
        const isTikTok = acc.provider === 'tiktok';
        const res = await fetch(`${API_BASE}/drupi/publish-tasks`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            catalog_video_id:  video.id,
            catalog_video_url: video.b2_url || undefined,
            platform:          acc.provider,
            social_id:         acc.socialId,
            caption:           title.trim(),
            ...(isTikTok ? tiktokSettings : {}),
          }),
          signal: ac.signal,
        });
        if (res.ok) {
          enqueued++;
        } else {
          const errData = await res.json().catch(() => ({}));
          const raw = errData?.detail || errData?.message || JSON.stringify(errData) || `Error ${res.status}`;
          setPhase('error');
          setPhaseMsg(publishErrorMessage(raw));
          return;
        }
      } catch {
        setPhase('error');
        setPhaseMsg('No se pudo encolar la publicación. Reintentá en unos segundos.');
        return;
      }
    }

    if (enqueued === 0) {
      setPhase('error');
      setPhaseMsg('No se pudo encolar la publicación.');
      return;
    }

    // P10 — non-blocking: close the modal and let the user keep browsing. A
    // global toast (survives this portal unmounting) tells them it's publishing
    // and that TikTok may take a few minutes to process & show it on the profile.
    // Audit requirement: "must clearly notify users that... it may take a few
    // minutes for the content to process and be visible on their profile."
    message.success({
      content: 'Tu video se está publicando. Puede tardar unos minutos en aparecer en tu perfil.',
      duration: 6,
    });
    onClose();
  };

  return ReactDOM.createPortal(
    <>
      <div
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 10000 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {contextHolder}
        <div
          style={{ background: '#fff', borderRadius: '20px 20px 0 0', padding: '24px 24px 40px', width: '100%', maxWidth: 520, position: 'relative', maxHeight: '85vh', overflowY: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <Text style={{ fontWeight: 700, fontSize: 17, color: tokens.colors.primary.neutral[900] }}>
              {ONLY_TIKTOK_DEMO ? 'Publicar en TikTok' : 'Publicar en tus redes'}
            </Text>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
              <X size={20} color={tokens.colors.primary.neutral[500]} />
            </button>
          </div>

          {/* Video thumbnail + caption side-by-side */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, margin: '0 0 20px' }}>
            <div style={{ flexShrink: 0, width: 90, borderRadius: 10, overflow: 'hidden', background: '#000', boxShadow: '0 2px 12px rgba(0,0,0,0.12)' }}>
              <div style={{ position: 'relative', paddingTop: '177.78%' }}>
                {/* `poster` is required for iOS Safari to show anything visible
                    before the user taps play: with HLS + preload='metadata',
                    Safari downloads only metadata (duration, codec) and never
                    decodes the first frame, leaving the visible area blank — and
                    the parent div's background:#000 shows through. The Modal v2
                    swipe player already uses this exact same pattern. */}
                <video
                  src={video.b2_url}
                  poster={video.poster}
                  preload='metadata'
                  muted
                  playsInline
                  // `controls` makes this a real, playable preview of the
                  // to-be-posted content (TikTok audit: "display a preview of
                  // the to-be-posted content") instead of just a static frame.
                  controls
                  onLoadedMetadata={e => {
                    const d = (e.currentTarget as HTMLVideoElement).duration;
                    if (Number.isFinite(d) && d > 0) setVideoDuration(d);
                  }}
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
            </div>
            {video.title && (
              <Text
                style={{
                  flex: 1,
                  fontSize: 13,
                  lineHeight: 1.4,
                  color: tokens.colors.primary.neutral[700],
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  // Clamp to ~7 lines (matches the ~160px thumbnail height with this font)
                  display: '-webkit-box',
                  WebkitLineClamp: 7,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {video.title}
              </Text>
            )}
          </div>

          {/* Descargar el video original (MP4 sin optimizar). El catálogo
              reproduce HLS; este botón baja el archivo original.
              Gateado por HIDE_DOWNLOAD_ORIGINAL (config/demo.ts): oculto por
              defecto — el endpoint de descarga sigue existiendo en el backend. */}
          {!HIDE_DOWNLOAD_ORIGINAL && (
            <button
              type='button'
              onClick={handleDownloadOriginal}
              disabled={downloading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', marginBottom: 20, padding: '10px 14px', borderRadius: 10,
                border: `1px solid ${tokens.colors.primary.neutral[300]}`,
                background: '#fff', cursor: downloading ? 'wait' : 'pointer',
                fontSize: 13, fontWeight: 600, color: tokens.colors.primary.neutral[700],
              }}
            >
              {downloading ? (
                <svg width='15' height='15' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' style={{ animation: 'spin 0.8s linear infinite' }}>
                  <path d='M21 12a9 9 0 1 1-6.219-8.56' />
                </svg>
              ) : (
                <Download size={15} />
              )}
              {downloading ? 'Preparando descarga…' : 'Descargar video original'}
            </button>
          )}

          {/* OAuth error — shown when user returns from a failed connection attempt */}
          {authError && (
            <Alert
              type='error'
              message={authError}
              closable
              onClose={clearAuthError}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          )}

          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: tokens.colors.primary.neutral[400], fontSize: 14 }}>
              Cargando cuentas...
            </div>
          ) : publishableAccounts.length === 0 ? (
            /* No accounts */
            <div style={{ padding: '20px', borderRadius: 12, background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.08)', textAlign: 'center' }}>
              {ONLY_TIKTOK_DEMO ? (
                <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 14, display: 'block' }}>
                  Necesitás conectar tu cuenta de TikTok para publicar este clip.
                </Text>
              ) : (
                <>
                  <Text style={{ color: tokens.colors.primary.neutral[500], fontSize: 14, display: 'block', marginBottom: 16 }}>
                    Necesitás conectar TikTok para publicar este clip.
                  </Text>
                  <Button
                    type='primary'
                    size='large'
                    onClick={openConnect}
                    style={{ background: tokens.colors.brand.gradient, border: 'none', fontWeight: 700, borderRadius: 10 }}
                  >
                    Conectar red social
                  </Button>
                  <Text style={{ color: tokens.colors.primary.neutral[400], fontSize: 12, display: 'block', marginTop: 10 }}>
                    Después de conectar, volvés directo a esta pantalla.
                  </Text>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Account chips + connect button — solo en modo completo */}
              {!ONLY_TIKTOK_DEMO && (
                <>
                  <Text style={{ fontWeight: 600, fontSize: 13, color: tokens.colors.primary.neutral[700], display: 'block', marginBottom: 12 }}>
                    Publicar en:
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 24 }}>
                    {publishableAccounts.map(a => (
                      <AccountChip key={a.id} account={a} selected={selectedIds.has(a.id)} onToggle={() => toggle(a.id)} />
                    ))}
                    <button
                      type='button'
                      onClick={openConnect}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', borderRadius: 999, border: `2px dashed ${tokens.colors.primary.neutral[300]}`, background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: tokens.colors.primary.neutral[600] }}
                    >
                      <Plus size={14} /> Conectar otra red
                    </button>
                  </div>
                </>
              )}

              {/* ── TikTok Content Posting API · compliance UX (audit) ──────
                  Solo se muestra cuando hay una cuenta TikTok seleccionada.
                  Renderiza sobre la creator-info fresca. Ver
                  TIKTOK_CONTENT_API_AUDIT.md. */}
              {hasTiktok && (
                <div style={{ marginBottom: 16 }}>
                  {creatorInfoLoading ? (
                    <div style={{ textAlign: 'center', padding: '16px 0' }}><Spin size='small' /></div>
                  ) : creatorInfoError ? (
                    <Alert type='error' message={creatorInfoError} style={{ borderRadius: 8 }} />
                  ) : publishBlock ? (
                    <Alert
                      type='warning'
                      showIcon
                      style={{ borderRadius: 8 }}
                      message={publishBlock.title || 'No podés publicar en TikTok ahora'}
                      description={publishBlock.message}
                    />
                  ) : creatorInfo ? (
                    <>
                      {/* P2 — nickname + avatar de la cuenta destino */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, padding: '10px 12px', borderRadius: 10, background: 'rgba(0,0,0,0.03)' }}>
                        {creatorInfo.creator_avatar_url ? (
                          <img src={creatorInfo.creator_avatar_url} alt='' style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 34, height: 34, borderRadius: '50%', background: tokens.colors.primary.neutral[200], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><TikTokIcon /></div>
                        )}
                        <div style={{ minWidth: 0 }}>
                          <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], display: 'block' }}>Publicando en TikTok como</Text>
                          <Text style={{ fontSize: 14, fontWeight: 700, color: tokens.colors.primary.neutral[900], display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {creatorInfo.creator_nickname || `@${firstTiktok?.username}`}
                          </Text>
                        </div>
                      </div>

                      {/* P7 — título editable */}
                      <div style={{ marginBottom: 16 }}>
                        <Text style={{ fontWeight: 600, fontSize: 13, color: tokens.colors.primary.neutral[700], display: 'block', marginBottom: 6 }}>Título</Text>
                        <Input.TextArea
                          value={title}
                          onChange={e => setTitle(e.target.value.slice(0, TIKTOK_TITLE_MAX))}
                          maxLength={TIKTOK_TITLE_MAX}
                          autoSize={{ minRows: 2, maxRows: 5 }}
                          placeholder='Escribí el título de tu publicación…'
                          disabled={busy}
                        />
                        <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], display: 'block', textAlign: 'right', marginTop: 2 }}>
                          {title.length}/{TIKTOK_TITLE_MAX}
                        </Text>
                      </div>

                      {/* P3 — privacidad: dropdown real, opciones de creator_info,
                          SIN valor por defecto (el user elige obligatoriamente). */}
                      <div style={{ marginBottom: 16 }}>
                        <Text style={{ fontWeight: 600, fontSize: 13, color: tokens.colors.primary.neutral[700], display: 'block', marginBottom: 6 }}>Quién puede ver este video</Text>
                        <Select
                          value={privacyLevel}
                          onChange={setPrivacyLevel}
                          style={{ width: '100%' }}
                          placeholder='Elegí quién puede verlo…'
                          disabled={busy}
                          // The ShareModal is a custom portal at z-index 10000;
                          // antd's dropdown popup defaults to ~1050 and would
                          // render BEHIND the modal (looks like it doesn't open).
                          // Raise the popup above the modal.
                          dropdownStyle={{ zIndex: 10050 }}
                          options={(creatorInfo.privacy_level_options || []).map(opt => ({
                            value: opt,
                            label: PRIVACY_LABEL[opt] || opt,
                            // Branded Content can't be private → disable SELF_ONLY
                            // while Branded Content is checked (TikTok rule).
                            disabled: opt === 'SELF_ONLY' && disclose && brandedContent,
                          }))}
                        />
                        {disclose && brandedContent && (
                          <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], display: 'block', marginTop: 4, lineHeight: 1.4 }}>
                            Branded content visibility cannot be set to private.
                          </Text>
                        )}
                      </div>

                      {/* P4 — interaction settings (desmarcados por defecto) */}
                      <div style={{ marginBottom: 16 }}>
                        <Text style={{ fontWeight: 600, fontSize: 13, color: tokens.colors.primary.neutral[700], display: 'block', marginBottom: 8 }}>Permitir interacciones</Text>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div>
                            <Checkbox checked={allowComment} disabled={busy || creatorInfo.comment_disabled} onChange={e => setAllowComment(e.target.checked)}>
                              Comentarios{creatorInfo.comment_disabled && <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400] }}> (deshabilitado en tu cuenta)</Text>}
                            </Checkbox>
                            <div style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], marginLeft: 24, marginTop: 2, lineHeight: 1.4 }}>
                              Otros pueden dejar comentarios en tu video.
                            </div>
                          </div>
                          <div>
                            <Checkbox checked={allowDuet} disabled={busy || creatorInfo.duet_disabled} onChange={e => setAllowDuet(e.target.checked)}>
                              Dúo{creatorInfo.duet_disabled && <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400] }}> (deshabilitado en tu cuenta)</Text>}
                            </Checkbox>
                            <div style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], marginLeft: 24, marginTop: 2, lineHeight: 1.4 }}>
                              Otros pueden grabar su propio video al lado del tuyo, en pantalla dividida.
                            </div>
                          </div>
                          <div>
                            <Checkbox checked={allowStitch} disabled={busy || creatorInfo.stitch_disabled} onChange={e => setAllowStitch(e.target.checked)}>
                              Stitch{creatorInfo.stitch_disabled && <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[400] }}> (deshabilitado en tu cuenta)</Text>}
                            </Checkbox>
                            <div style={{ fontSize: 11, color: tokens.colors.primary.neutral[400], marginLeft: 24, marginTop: 2, lineHeight: 1.4 }}>
                              Otros pueden recortar un fragmento de tu video y usarlo dentro del suyo.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* P6 — divulgación de contenido comercial */}
                      <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 10, background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.08)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                          <div style={{ minWidth: 0 }}>
                            <Text style={{ fontWeight: 600, fontSize: 13, color: tokens.colors.primary.neutral[900], display: 'block' }}>Divulgar contenido comercial</Text>
                            <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[500], display: 'block', lineHeight: 1.4 }}>Activá si el video promociona una marca o producto.</Text>
                          </div>
                          <Switch checked={disclose} onChange={setDisclose} disabled={busy} />
                        </div>
                        {disclose && (
                          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            <Checkbox checked={yourBrand} disabled={busy} onChange={e => setYourBrand(e.target.checked)}>
                              <Text style={{ fontSize: 13, fontWeight: 600 }}>Tu marca</Text>
                              <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[500], display: 'block' }}>Promocionás tu propio negocio. Se etiqueta como "Contenido promocional".</Text>
                            </Checkbox>
                            <Tooltip title={!brandedContentAllowed ? "Branded content visibility cannot be set to private." : ''}>
                              <span>
                                <Checkbox checked={brandedContent} disabled={busy || !brandedContentAllowed} onChange={e => setBrandedContent(e.target.checked)}>
                                  <Text style={{ fontSize: 13, fontWeight: 600 }}>Contenido de marca</Text>
                                  <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[500], display: 'block' }}>
                                    Promocionás a un tercero. Se etiqueta como "Asociación pagada".
                                    {!brandedContentAllowed && ' (Requiere visibilidad pública o de amigos.)'}
                                  </Text>
                                </Checkbox>
                              </span>
                            </Tooltip>
                            {!commercialOk && (
                              <Text style={{ fontSize: 11, color: '#c00' }}>Elegí al menos una opción para poder publicar.</Text>
                            )}
                            {/* Per-option label prompt (TikTok exact wording):
                                Your Brand → "Promotional content"; Branded Content
                                (or both) → "Paid partnership". */}
                            {(yourBrand || brandedContent) && (
                              <div style={{ fontSize: 11, color: tokens.colors.primary.neutral[600], background: 'rgba(51,63,131,0.06)', borderRadius: 8, padding: '8px 10px' }}>
                                {brandedContent
                                  ? <>Tu video se etiquetará como <strong>«Asociación pagada»</strong> (Paid partnership).</>
                                  : <>Tu video se etiquetará como <strong>«Contenido promocional»</strong> (Promotional content).</>
                                }
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* P8 — aviso si el video excede el máximo de la cuenta */}
                      {!durationOk && (
                        <Alert
                          type='warning'
                          showIcon
                          style={{ borderRadius: 8, marginBottom: 16 }}
                          message={`Este video dura ${Math.round(videoDuration || 0)}s y tu cuenta de TikTok permite hasta ${maxDur}s. Probá con un clip más corto.`}
                        />
                      )}
                    </>
                  ) : null}
                </div>
              )}

              {/* Nota de plataforma */}
              <div style={{ marginBottom: 16, fontSize: 12, color: tokens.colors.primary.neutral[400] }}>
                {ONLY_TIKTOK_DEMO ? (
                  <span>📱 Se publica como <strong>Reel</strong> en TikTok.</span>
                ) : (
                  <>
                    {selectedAccounts.some(a => a.provider === 'tiktok') && (
                      <span>📱 TikTok: se publica como <strong>Reel</strong>.</span>
                    )}
                    {selectedAccounts.some(a => a.provider === 'instagram') && (
                      <span style={{ display: 'block', marginTop: 2 }}>📸 Instagram: se publica como <strong>Reel</strong>.</span>
                    )}
                  </>
                )}
              </div>

              {/* P5 — declaración Music Usage Confirmation (+ Branded Content si aplica), antes del botón */}
              {hasTiktok && creatorInfo && !creatorInfoError && !publishBlock && (
                <Text style={{ fontSize: 11, color: tokens.colors.primary.neutral[500], display: 'block', marginBottom: 12, lineHeight: 1.5 }}>
                  Al publicar, aceptás{' '}
                  {disclose && brandedContent && (
                    <>
                      la{' '}
                      <a href='https://www.tiktok.com/legal/page/global/bc-policy/en' target='_blank' rel='noopener noreferrer'>Política de Contenido de Marca</a>
                      {' '}y{' '}
                    </>
                  )}
                  la{' '}
                  <a href='https://www.tiktok.com/legal/page/global/music-usage-confirmation/en' target='_blank' rel='noopener noreferrer'>Confirmación de Uso de Música</a>
                  {' '}de TikTok.
                </Text>
              )}

              {/* Progress area */}
              {phase !== 'idle' && (
                <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 12, background: phase === 'error' ? 'rgba(255,0,0,0.04)' : 'rgba(51,63,131,0.05)', border: `1px solid ${phase === 'error' ? 'rgba(255,0,0,0.15)' : 'rgba(51,63,131,0.12)'}` }}>
                  {phase === 'enqueueing' && (
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ height: 4, borderRadius: 4, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 4, background: tokens.colors.brand.gradient, width: '40%', transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  )}
                  <Text style={{ fontSize: 13, fontWeight: 600, color: phase === 'error' ? '#c00' : tokens.colors.primary.neutral[700] }}>
                    {phaseMsg}
                  </Text>
                </div>
              )}

              {/* Disabled-publish hover: when the commercial toggle is on but no
                  option is selected, TikTok requires this exact notification. */}
              <Tooltip
                title={
                  disclose && !yourBrand && !brandedContent
                    ? 'You need to indicate if your content promotes yourself, a third party, or both.'
                    : ''
                }
              >
                <button
                  disabled={!canPublish}
                  onClick={handlePublish}
                  style={{
                    width: '100%',
                    background: canPublish ? tokens.colors.brand.gradient : tokens.colors.primary.neutral[200],
                    border: 'none',
                    fontWeight: 700,
                    height: 48,
                    fontSize: 15,
                    borderRadius: 12,
                    color: 'white',
                    cursor: !canPublish ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                >
                  {busy && (
                    <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' strokeWidth='2.5' strokeLinecap='round' style={{ animation: 'spin 0.8s linear infinite' }}>
                      <path d='M21 12a9 9 0 1 1-6.219-8.56' />
                    </svg>
                  )}
                  {busy
                    ? 'Enviando...'
                    : selectedIds.size === 0
                    ? 'Seleccioná al menos una red'
                    : !titleOk
                    ? 'Escribí un título'
                    : !privacyOk
                    ? 'Elegí quién puede ver el video'
                    : !commercialOk
                    ? 'Indicá el tipo de contenido comercial'
                    : ONLY_TIKTOK_DEMO
                    ? 'Publicar en TikTok'
                    : `Publicar en ${selectedIds.size} red${selectedIds.size > 1 ? 'es' : ''}`
                  }
                </button>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      {/* ConnectModal — z-index must exceed the ShareModal backdrop (10000) */}
      {!ONLY_TIKTOK_DEMO && (
        <ConnectModal open={showConnect} onClose={() => setShowConnect(false)} zIndex={10001} />
      )}
    </>,
    document.body
  );
};
